<template>
    <section class="team-hero" :style="{ '--team-color': team.color || '#009688' }">
        <!-- the team photo behind the banner: a click opens it whole -->
        <button
            v-if="photoUrl"
            type="button"
            class="team-hero__photo"
            :style="{ backgroundImage: `url(&quot;${photoUrl}&quot;)`, '--photo-ratio': photoRatio }"
            aria-label="Φωτογραφία ομάδας"
            @click="photoOpen = true"
        />
        <button v-if="photoUrl" type="button" class="team-hero__photo-chip" @click="photoOpen = true">
            <v-icon small color="white">mdi-magnify-plus-outline</v-icon>
            Φωτογραφία ομάδας
        </button>
        <div class="team-hero__inner">
            <!-- the colour of the team behind its logo -->
            <div class="team-hero__badge">
                <span class="cl-logo team-hero__logo">
                    <img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" :alt="team.name">
                    <v-icon v-else size="72" color="teal"> mdi-basketball-hoop </v-icon>
                </span>
            </div>

            <div class="team-hero__text">
                <div class="team-hero__league">
                    <v-menu v-if="team.leagues && team.leagues.length > 1" offset-y dark>
                        <template #activator="{ on, attrs }">
                            <button type="button" class="cl-chip cl-chip--gradient team-hero__picker" v-bind="attrs" v-on="on">
                                {{ selectedLeague ? selectedLeague.title : (team.league ? team.league.title : '') }}
                                <v-icon x-small color="white">mdi-chevron-down</v-icon>
                            </button>
                        </template>
                        <v-list dense color="#232323">
                            <v-list-item
                                v-for="league in team.leagues"
                                :key="league.id"
                                @click="$emit('setLeague', league)"
                            >
                                <v-list-item-title>{{ league.title }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <span v-else-if="team.league" class="cl-chip cl-chip--gradient">{{ team.league.title }}</span>
                </div>
                <h1 class="team-hero__name">{{ team.name }}</h1>
                <div class="cl-kpis">
                    <div class="cl-kpi cl-kpi--accent">
                        <div class="cl-kpi__value">{{ wins }}</div>
                        <div class="cl-kpi__label">Νίκες</div>
                    </div>
                    <div class="cl-kpi">
                        <div class="cl-kpi__value">{{ losses }}</div>
                        <div class="cl-kpi__label">Ήττες</div>
                    </div>
                    <div class="cl-kpi">
                        <div class="cl-kpi__value">{{ wins + losses }}</div>
                        <div class="cl-kpi__label">Αγώνες</div>
                    </div>
                    <div class="cl-kpi">
                        <div class="cl-kpi__value">{{ winPercentage }}</div>
                        <div class="cl-kpi__label">Νίκες %</div>
                    </div>
                </div>
                <!-- the controls under the numbers: the right side is the photo's -->
                <div class="team-hero__aside">
                    <match-type-choice :counts="phaseCounts" @setType="setType" />
                    <!-- the schedule of the team as a calendar subscription, as on the big site -->
                    <team-calendar-button v-if="team.id" :team-id="team.id" />
                    <button v-if="photoUrl" type="button" class="team-hero__photo-btn team-hero__photo-btn--phone" @click="photoOpen = true">
                        <span class="team-hero__photo-thumb" :style="{ backgroundImage: `url(&quot;${photoUrl}&quot;)` }">
                            <v-icon small color="white">mdi-magnify-plus-outline</v-icon>
                        </span>
                        <span class="team-hero__photo-copy">
                            <span class="team-hero__photo-title">Φωτογραφία ομάδας</span>
                            <span class="team-hero__photo-sub">Δες την σε πλήρες μέγεθος</span>
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <!-- the photo whole, on a dark backdrop -->
        <v-dialog v-if="photoUrl" v-model="photoOpen" max-width="1200" content-class="team-photo-dialog">
            <div class="team-photo">
                <div class="team-photo__bar">
                    <span class="cl-logo team-photo__logo"><img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt=""></span>
                    <span class="team-photo__name">{{ team.name }}</span>
                    <v-spacer />
                    <a :href="photoUrl" target="_blank" rel="noopener" class="team-photo__btn" title="Άνοιγμα σε νέα καρτέλα">
                        <v-icon small color="white">mdi-open-in-new</v-icon>
                    </a>
                    <button type="button" class="team-photo__btn" title="Κλείσιμο" @click="photoOpen = false">
                        <v-icon small color="white">mdi-close</v-icon>
                    </button>
                </div>
                <img :src="photoUrl" :alt="team.name" class="team-photo__img">
            </div>
        </v-dialog>
    </section>
</template>

<script>
    import MatchTypeChoice from '@/components/MatchTypeChoice'
    import TeamCalendarButton from '@/components/TeamCalendarButton'
    export default {
        components: { MatchTypeChoice, TeamCalendarButton },
        props: {
            team: {
                type: Object,
                default: null
            },
            selectedLeague: {
                type: Object,
                default: null
            },
            // matches per phase (regular season, play offs, cup), for the hint on the phase buttons
            phaseCounts: {
                type: Array,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                photoOpen: false,
                // width / height of the photo: its box on the banner has its shape, so all of it shows
                photoRatio: 1.5
            }
        },
        computed: {
            // the file names have spaces and brackets ("25-26 NN Hellas.jpg"), so they are encoded for the url
            photoUrl() {
                return this.team && this.team.image ? `${this.assetsUrl}/${encodeURI(this.team.image)}` : null
            },
            wins() {
                return this.team.seasonWins || 0
            },
            losses() {
                return this.team.seasonLoses || 0
            },
            winPercentage() {
                const games = this.wins + this.losses
                return games ? `${Math.round(this.wins / games * 100)}%` : '-'
            }
        },
        watch: {
            photoUrl: {
                immediate: true,
                handler(url) {
                    if (!url) return
                    const image = new Image()
                    image.onload = () => {
                        if (url === this.photoUrl && image.naturalWidth && image.naturalHeight) {
                            this.photoRatio = image.naturalWidth / image.naturalHeight
                        }
                    }
                    image.src = url
                }
            }
        },
        methods: {
            setType(option) {
                this.$emit('setType', option)
            }
        }
    }
</script>

<style scoped>
.team-hero {
    position: relative;
    overflow: hidden;
    color: #fff;
    text-align: left;
    background:
        radial-gradient(circle at 90% 0%, rgba(3, 229, 183, 0.25) 0%, rgba(3, 229, 183, 0) 45%),
        #232323;
}

.team-hero::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

/* the team photo, faded into the charcoal on the right: all of it (no one cut out), the room around it filled
   with the same photo blurred */
.team-hero__photo {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 70%;
    padding: 0;
    border: 0;

    /* the photo of the style is drawn by the layer below */
    background: transparent;
    background-size: 0 0;
    background-repeat: no-repeat;
    overflow: hidden;
    opacity: 0.45;
    cursor: zoom-in;
    transition: opacity 0.2s;
}

/* all of the photo, in a box of its shape on the right, faded into the charcoal from its own left edge */
.team-hero__photo::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    max-width: 100%;
    aspect-ratio: var(--photo-ratio, 1.5);
    background-image: inherit;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.35) 22%, #000 55%);
    mask-image: linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.35) 22%, #000 55%);
}

.team-hero__inner {
    position: relative;
    display: flex;
    align-items: center;
    gap: 32px;
    max-width: 1240px;
    margin: 0 auto;
    padding: 36px 24px;
}

.team-hero__badge {
    position: relative;
    flex: 0 0 auto;
    padding: 18px 34px 18px 18px;
}

/* the slanted panel in the colour of the team */
.team-hero__badge::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 12px;
    background: var(--team-color);
    transform: skewX(-8deg);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
}

.team-hero__logo {
    position: relative;
    width: 150px;
    height: 150px;
    border-radius: 12px;
}

.team-hero__text {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 680px;
}

.team-hero__picker {
    cursor: pointer;
}

.team-hero__name {
    margin: 10px 0 16px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 3.2rem;
    font-weight: 400;
    line-height: 1;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    overflow-wrap: anywhere;
}

.team-hero__aside {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 16px;
}

/* the photo opens from a chip on it (a phone: from the button with the thumb) */
.team-hero__photo-btn.team-hero__photo-btn--phone {
    display: none;
}

.team-hero__photo-chip {
    position: absolute;
    right: 16px;
    bottom: 18px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px 5px 9px;
    border-radius: 999px;
    background: rgba(35, 35, 35, 0.72);
    border: 1px solid rgba(255, 255, 255, 0.22);
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 700;
    backdrop-filter: blur(4px);
    transition: background 0.15s, border-color 0.15s;
}

.team-hero__photo-chip:hover {
    background: rgba(3, 229, 183, 0.25);
    border-color: #03e5b7;
}

.team-hero__photo:hover {
    opacity: 0.65;
}

.team-hero__photo-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 12px 5px 5px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    text-align: left;
    transition: background 0.15s, border-color 0.15s;
}

.team-hero__photo-btn:hover {
    background: rgba(3, 229, 183, 0.14);
    border-color: #03e5b7;
}

.team-hero__photo-thumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    aspect-ratio: 16 / 10;
    border-radius: 7px;
    background-size: cover;
    background-position: center;
    box-shadow: inset 0 0 0 100px rgba(0, 0, 0, 0.25);
}

.team-hero__photo-copy {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
}

.team-hero__photo-title {
    font-size: 0.8rem;
    font-weight: 700;
}

.team-hero__photo-sub {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.6);
}

.team-photo {
    border-radius: 14px;
    background: #232323;
    color: #fff;
    overflow: hidden;
}

.team-photo__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px 10px 14px;
    border-bottom: 2px solid rgba(3, 229, 183, 0.35);
}

.team-photo__logo {
    width: 32px;
    height: 32px;
}

.team-photo__name {
    min-width: 0;
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.4rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.team-photo__btn {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    text-decoration: none;
}

.team-photo__btn:hover {
    background: rgba(255, 255, 255, 0.22);
}

.team-photo__img {
    display: block;
    width: 100%;
    max-height: calc(90vh - 60px);
    object-fit: contain;
    background: #111;
}

/* a narrower screen: the calendar under the phases, the photo keeps its side */
@media (max-width: 1500px) {
    .team-hero__text {
        max-width: 440px;
    }
}

@media (max-width: 900px) {
    .team-hero__inner {
        flex-wrap: wrap;
        gap: 18px;
        padding: 24px 16px;
    }

    .team-hero__logo {
        width: 100px;
        height: 100px;
    }

    .team-hero__name {
        font-size: 2.2rem;
    }

    /* a phone: the photo across the top, all of it, fading into the charcoal under it */
    .team-hero__photo {
        width: 100%;
        bottom: auto;
        aspect-ratio: var(--photo-ratio, 1.5);
        opacity: 0.5;
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -webkit-mask-image: linear-gradient(180deg, #000 55%, transparent 100%);
        mask-image: linear-gradient(180deg, #000 55%, transparent 100%);
    }

    .team-hero__photo::after {
        left: 0;
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -webkit-mask-image: none;
        mask-image: none;
    }

    .team-hero__aside {
        flex-direction: column;
        align-items: flex-start;
    }

    .team-hero__photo-btn.team-hero__photo-btn--phone {
        display: flex;
    }

    .team-hero__photo-chip {
        display: none;
    }

    /* the phases scroll instead of being cut on a phone */
    .team-hero__aside >>> .cl-segment {
        max-width: 100%;
        overflow-x: auto;
        scrollbar-width: none;
    }
}
</style>
